import type { HTMLAttributes, ReactNode } from 'react'
import cx from 'classnames'
import { match } from '@/utils/pattern-match-util'
import styles from './NotificationBar.module.css'

interface NotificationBarProps {
  children?: ReactNode
  type?: 'info' | 'error'
  centered?: boolean
  testId?: string
}

/**
 * A bar that shows a notice across the top of a view.
 */
export function NotificationBar({
  children,
  testId,
  centered = false,
  type = 'info',
}: NotificationBarProps) {
  return (
    <div
      className={cx(
        styles.root,
        match(type, { info: undefined, error: styles.error }),
        centered && styles.centered,
      )}
      data-test={testId}
    >
      {children}
    </div>
  )
}

interface NotificationBarTitleProps extends HTMLAttributes<HTMLSpanElement> {
  children?: ReactNode
  testId?: string
}

/**
 * The title of a notification bar.
 */
export function NotificationBarTitle({
  children,
  testId,
  ...rest
}: NotificationBarTitleProps) {
  return (
    <span
      className={cx(styles.text, styles.title)}
      data-test={testId}
      {...rest}
    >
      {children}
    </span>
  )
}

interface NotificationBarSubtitleProps extends HTMLAttributes<HTMLSpanElement> {
  children?: ReactNode
  testId?: string
}

/**
 * The subtitle of a notification bar.
 */
export function NotificationBarSubtitle({
  children,
  testId,
  ...rest
}: NotificationBarSubtitleProps) {
  return (
    <span
      className={cx(styles.text, styles.subtitle)}
      data-test={testId}
      {...rest}
    >
      {children}
    </span>
  )
}

interface NotificationBarActionProps extends HTMLAttributes<HTMLSpanElement> {
  children?: ReactNode
  testId?: string
}

/**
 * A link in a notification bar.
 */
export function NotificationBarAction({
  children,
  testId,
  ...rest
}: NotificationBarActionProps) {
  return (
    <a className={cx(styles.text, styles.action)} data-test={testId} {...rest}>
      {children}
    </a>
  )
}
